import { ServiceAgreementModalUploadForm, ServiceAgreementUploadModalProps } from "../types";
import React from "react"
import { Modal } from "react-bootstrap";
import ThemedModal from "../../../utilities/ThemedModal";
import { Button } from "../../../";
import { Control, Controller, UseFieldArrayReturn, UseFormSetValue } from "react-hook-form";
import { DropzoneOptions, FileRejection, useDropzone } from "react-dropzone";
import { useForm, FormProvider } from "react-hook-form";
import { FileUploadForm } from "./FileUploadForm";
import { DateInput } from "./../../../forms"
import { DocumentDetailsCard } from "../../../utilities/DocumentDetailsCard";
import { useUploadEvidence } from "../hooks/useUploadEvidence";

export const DropzoneBaseOptions: DropzoneOptions = {
    maxFiles: 5,
    accept: {
        "application/msword": [],
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [],
        "text/csv": [],
        "image/gif": [],
        "image/jpeg": [],
        "image/png": [],
        "application/pdf": [],
        "application/vnd.ms-excel": [],
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
        "application/xml": [],
        "text/plain": [],
        "video/mp4": [],
        "video/mpeg": [],
        "video/x-msvideo": [],
        "application/vnd.ms-outlook": [],
        "message/rfc822": []
    }
};

export const ServiceAgreementUploadModal = ({
    backdrop = 'static',
    cancel,
    centered = true,
    defaultValues,
    feeAgreementId,
    dialogId,
    dismiss,
    proceed,
    show,
    size = "lg",
    ...rest
} : Readonly<ServiceAgreementUploadModalProps>) => {

    const { uploadEvidence } = useUploadEvidence();

    const formMethods = useForm<ServiceAgreementModalUploadForm>();

    const { handleSubmit, setValue, reset, watch, control } = formMethods;
    const fileData = watch('fileData');
    const signedDate = watch('signedDate');

    const onSubmit = (form: ServiceAgreementModalUploadForm) => {
        console.log(form);
        uploadEvidence(form).then((r) => {
            if (r.status < 300) {
                proceed!(r);
            }
        });
    };

    const onClose = async () : Promise<void> => {
        dismiss!();
    };

    const onDrop = React.useCallback(async (acceptedFiles: File[] ) => {
        if (acceptedFiles.length === 0) {
            return;
        }
        setValue('fileData', acceptedFiles[0], { shouldDirty: true });
    }, []);

    const onClearFiles = () => {
        setValue('fileData', null, { shouldDirty: true });
    };

    const dropzone = useDropzone({ ...DropzoneBaseOptions, onDrop });

       // whenever the file data is changed
        React.useEffect(() => {
            if (!fileData) {
                reset({ ...defaultValues });
                return;
            }
    
            reset({
                ...defaultValues,
                fileData: fileData,
                feeAgreementId: feeAgreementId,
                signedDate: null
            });
        }, [ fileData ]);

    return ( 
        <Modal className="overflow-visible" backdrop={backdrop} centered={centered} size={size} show={show} onHide={onClose} {...rest}>
            <ThemedModal.Header>
                <Modal.Title>
                    Upload Service Agreement
                </Modal.Title>
            </ThemedModal.Header>
            <FormProvider {...formMethods}>   
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Modal.Body className="p-0">
                        <section className="p-3">
                        <FileUploadForm dropzone={dropzone} isLoading={false} />
                        {!dropzone.isFileDialogActive && (
                            <>
                                {fileData && (
                                    <>
                                        <DocumentDetailsCard
                                            clearFile={onClearFiles}
                                            fileName={fileData.name}
                                            fileSize={fileData.size}
                                            type={1000} 
                                        />

                                        <div className="row mb-3 mt-3">
                                            <div className="col">
                                                <Controller
                                                    name="signedDate"
                                                    control={control}
                                                    rules={{ 
                                                        required: 'The signed date must be populated'
                                                    }}
                                                    render={({ field: { ref, value, onBlur, ...rest } }) => (
                                                        <DateInput
                                                            label="Signed Date"
                                                            value={value}
                                                            isClearable={true}
                                                            onBlur={(value) => setValue('signedDate', value)}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                        </section>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button type="submit" variant="primary" disabled={!signedDate || !fileData}>Upload</Button>
                        <Button type="button" variant="light" onClick={onClose}>Close</Button>
                    </Modal.Footer>
                </form>
            </FormProvider>
        </Modal>
    );

};